import { CustomizationOptions } from '@ally/data-types-customization'
import { withAppHeaders, withContentType, toJSON } from '@ally/fido'
import { flow } from '@ally/utilitarian'
import { useHostServices } from '../../HostServices'
import { CustomizeDataObj } from '../types'
import { NEVER_SAVED_CUSTOMIZATIONS } from '../customizationContext/utils'

interface useCustomizationDataHook {
  updateCustomization: (
    payload: Partial<CustomizeDataObj>,
  ) => Promise<{ payload: CustomizationOptions | null }>
}

export const useUpdateCustomizationData = (
  customizationId: string,
): useCustomizationDataHook => {
  const { session, featureFlags } = useHostServices()
  const apiVersion = featureFlags.variation('FF_afg-customization-version', 1)
  const { guid, access_token: accessToken } = session.data || {}

  const newUser = customizationId === NEVER_SAVED_CUSTOMIZATIONS
  const method = newUser ? 'POST' : 'PATCH'
  const afgVersionUrl = `/v${apiVersion}`
  const endpointUrl = newUser
    ? `/acs${afgVersionUrl}/customers/${guid}/customizations`
    : `/acs${afgVersionUrl}/customers/${guid}/customizations/${customizationId}`

  const updateCustomization = (
    payload: Partial<CustomizeDataObj>,
  ): Promise<{ payload: CustomizationOptions | null }> => {
    if (!accessToken || !guid) {
      return Promise.reject(
        new Error('customization patch failed: session data is null'),
      )
    }

    return fetch(
      endpointUrl,
      flow(
        withAppHeaders,
        withContentType,
      )({
        method,
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      }),
    )
      .then(response => {
        if (!response.ok) {
          throw new Error(
            `Customization ${method} failed with status of ${response.status}`,
          )
        }

        return method === 'POST' ? toJSON<CustomizationOptions>(response) : null
      })
      .then(maybeCustomizationOptions => {
        return maybeCustomizationOptions
          ? { payload: maybeCustomizationOptions }
          : { payload: null }
      })
  }
  return { updateCustomization }
}
